/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable unicorn/no-null */
/* eslint-disable @typescript-eslint/no-unused-expressions */

import { GetQuestionsResponse, GetStatsResponse } from '@/hooks/api/useCueingEngine';
import { ContentItem, PracticeContentResponse } from '@/hooks/api/usePractice';
import logger from '@/logger';
import {
  ArrangeTheFollowingFormat,
  ErrorAnalysisFormat,
  Explanation,
  ExplanationTypeEnum,
  FillInTheBlanksFormat,
  Hint,
  HintTypeEnum,
  MatchTheFollowingFormat,
  McqFormat,
  MediumMapping,
  OneWordFormat,
  QuestionFormatEnum,
  QuestionType,
  SequencingFormat,
  SubjectiveRearrangeFormat,
} from '@/pages/TaskPlayer/types';

const validationFunctionForMatchTheFollowing = (data: MatchTheFollowingFormat) => {
  const validTypes = new Set(['text', 'image']);
  const errors: string[] = [];
  if (!validTypes.has(data.answerType) || !validTypes.has(data.questionType)) {
    errors.push(
      'Invalid question format, questionType and answerType must be either "text" or "image" ',
    );
  }
  if (data.questionType !== data.answerType) {
    errors.push('Type of questionType and answerType must be same');
  }
  if (data.questionData.length === 0 || data.answerData.length === 0) {
    errors.push('Question data or answer data can not be null');
  }
  if (data.questionData.length !== data.answerData.length) {
    errors.push('Length of questionData and answerData must be same');
  }
  if (data.questionData.length === 1 && data.answerData.length === 1) {
    errors.push('Length of questionData and answerData must be greater than 1.');
  }
  return errors.length > 0
    ? { isValid: false, message: errors }
    : { isValid: true, message: ['Valid question format'] };
};

const validationFunctionForOneWord = (data: OneWordFormat) => {
  if (data.answers && data.answers.length > 0) {
    return {
      isValid: true,
      message: ['Valid question format'],
    };
  }
  return {
    isValid: false,
    message: ['Length of answer array can not be empty'],
  };
};

const validationFunctionForMcq = (data: McqFormat) => {
  const errors: string[] = [];
  if (!Array.isArray(data.options) || data.options.length === 0) {
    errors.push('options should be a non-empty array.');
  } else {
    for (const [index, option] of data.options.entries()) {
      const validTypes = ['text', 'audio', 'image'];
      if (!validTypes.includes(option.type)) {
        errors.push(`Invalid 'type' at index ${index}`);
      }
      if (typeof option.value !== 'string' || option.value.trim() === '') {
        errors.push(`'Value' at index ${index} must be a non-empty string.`);
        return {
          isValid: false,
          message: errors,
        };
      }
      const validEvidenceTypes = ['eom', 'eoc', null];
      if (!validEvidenceTypes.includes(option.evidenceType)) {
        errors.push(`Invalid 'evidenceType' at index ${index}`);
      }
      if (option.evidenceId !== null && typeof option.evidenceId !== 'string') {
        errors.push(`'evidenceId' at index ${index} must be a string or null.`);
      }
      const validExplanationTypes = ['image', 'text', 'audio', 'video', 'pdf', null];
      if (!validExplanationTypes.includes(option.explanationType)) {
        errors.push(
          `Invalid explanationType, explanation type must be 'image', 'text','audio','video','pdf' or null `,
        );
      }
      if (option.explanation !== null && typeof option.explanation !== 'string') {
        errors.push(`'Explanation' at index ${index} must be a string or null.`);
      }
    }
    const noOfOptions = data.options.length;
    if (noOfOptions <= 1 || noOfOptions > 4) {
      errors.push('Number of option must be 2,3 or 4');
    }
    const optionValues = data.options.map((option) => option.value.toLowerCase());
    const uniqueOptions = new Set(optionValues);
    if (uniqueOptions.size !== optionValues.length) {
      errors.push('All options must be unique.');
    }
    const allOptionsTypes = data.options.map((option) => option.type);
    const validTypes = new Set(['text', 'audio', 'image']);
    const allValidOptionTypes = allOptionsTypes.every((type) => validTypes.has(type));
    if (allValidOptionTypes && new Set(allOptionsTypes).size > 1) {
      errors.push('All options must have the same type: "text", "audio", or "image".');
    }

    const answers = data.options.filter((option) => option.isCorrect);
    if (answers.length === 0) {
      errors.push('At least one option must be marked as correct.');
    }
    if (answers.length > 1) {
      errors.push('Only one option can be marked as correct.');
    }
  }
  return errors.length > 0
    ? { isValid: false, message: errors }
    : { isValid: true, message: ['Valid question format'] };
};

const validationFunctionForFillInTheBlanks = (data: FillInTheBlanksFormat) => {
  const errors: string[] = [];
  if (data.questionText === null || data.answerText === null) {
    errors.push('Invalid question format, questionText or answerText can not be null');
  }
  const noOfBlanks = data.questionText.split('#').length - 1;
  const correctAnswerArray: string[] = data.answerText
    ? data.answerText
        .split('$$')
        .filter((answer) => answer !== null)
        .map((answer) => answer.trim())
        .filter((answer) => answer !== '')
    : [];
  if (noOfBlanks === 0 || correctAnswerArray.length !== noOfBlanks) {
    errors.push(
      `Number of blanks space can not be 0 and it's length must be equal to answer length array `,
    );
  }
  return errors.length > 0
    ? { isValid: false, message: errors }
    : { isValid: true, message: ['Valid question format'] };
};

const validationFunctionForErrorAnalysis = (data: ErrorAnalysisFormat) => {
  const errors: string[] = [];
  if (!Array.isArray(data.options) || data.options.length === 0) {
    errors.push('Invalid question format, options length cannot be 0');
  }
  if (data.options)
    for (const [index, option] of data.options.entries()) {
      const validTypes = new Set(['text', 'image']);
      if (!validTypes.has(option.type)) {
        errors.push(`Type of option at index ${index} must be either 'text' or 'image'`);
      }
      if (typeof option.value !== 'string' || option.value.trim() === '') {
        errors.push(`Option's text or image at index ${index} cannot be null or empty`);
      }
      if (typeof option.isCorrect !== 'boolean') {
        errors.push(`Type of isCorrect must be boolean.`);
      }
      if (option.correctType && !validTypes.has(option.correctType)) {
        errors.push(`Type of correctType at index ${index} must be either 'text' or 'image'`);
      }
      if (option.correctValue && typeof option.correctValue !== 'string') {
        errors.push(`Option's correct value at index ${index} must be of type string.`);
      }
    }
  return errors.length > 0
    ? { isValid: false, message: errors }
    : { isValid: true, message: ['Valid question format'] };
};

const validationFunctionForSequencing = (data: SequencingFormat) => {
  const errors: string[] = [];
  const validType = ['text'];
  if (!data.options || data.options.length === 0) {
    errors.push('Options length cannot be 0.');
  } else {
    if (data.options.length === 1) {
      errors.push('Length of data.options must be atleast 2.');
    }
    for (const [index, option] of data.options.entries()) {
      if (typeof option !== 'string') {
        errors.push(`Type of option must be a string at index ${index}`);
      }
      if (option.trim() === '') {
        errors.push(`Option can not be null or empty at index ${index}`);
      }
    }
  }
  if (!validType.includes(data.type)) {
    errors.push('Invalid type it should be text');
  }
  return errors.length > 0
    ? { isValid: false, message: errors }
    : { isValid: true, message: ['Valid question format'] };
};

const validationFunctionForArrangeTheFollowing = (data: ArrangeTheFollowingFormat) => {
  const errors: string[] = [];
  const validType = ['text', 'image'];
  if (!validType.includes(data.type)) {
    errors.push(`Invalid type, type must be either 'text' or 'image'`);
  }
  if (!data.options || data.options.length === 0) {
    errors.push('Invalid question format, option length can not be 0');
  } else if (data.options.length === 1) {
    errors.push('Invalid type, length of data must be greater than 1.');
  }
  return errors.length > 0
    ? { isValid: false, message: errors }
    : { isValid: true, message: ['Valid question format'] };
};

const validationFunctionSubjectRearrange = (data: SubjectiveRearrangeFormat) => {
  const errors: string[] = [];
  if (
    !data.steps ||
    !data.distractors ||
    data.steps.length === 0 ||
    data.distractors.length === 0
  ) {
    errors.push('steps and distractors length can not be 0');
  }
  let noOfIsMovable = 0;
  if (data.steps)
    for (const [index, step] of data.steps.entries()) {
      if (typeof step.value !== 'string' || step.value.trim() === '') {
        errors.push(`Step at index ${index} must have a valid non-empty string.`);
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      if (step.type !== 'text') {
        errors.push(`Step at index ${index} must have type 'text'.`);
      }
      if (step.isMovable === true) {
        noOfIsMovable += 1;
      }
    }
  if (noOfIsMovable === 0) {
    errors.push(`Atleast one droppable area must be empty `);
  }
  if (data.distractors)
    for (const [index, distractor] of data.distractors.entries()) {
      if (typeof distractor.value !== 'string' || distractor.value.trim() === '') {
        errors.push(`Distractor at index ${index} must have a valid non-empty string.`);
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      if (distractor.type !== 'text') {
        errors.push(`Distractor at index ${index} must have type 'text'.`);
      }
    }
  return errors.length > 0
    ? { isValid: false, message: errors }
    : { isValid: true, message: ['Valid question format'] };
};

const questionTextValidation = (data: MediumMapping) => {
  const errors: string[] = [];
  if (typeof data.questionText !== 'string') {
    errors.push('Type of questionText must be string');
  }
  if (!data.questionText || data.questionText.trim() === '') {
    errors.push('questionText can not be null or empty');
  }
  return errors.length > 0
    ? { isValid: false, message: errors }
    : { isValid: true, message: ['Valid questionText'] };
};

const questionMediaTypeValidation = (data: MediumMapping) => {
  const errors: string[] = [];
  const validTypes = ['image', 'audio', null];
  if (!validTypes.includes(data.questionMediaType)) {
    errors.push('Question media type must be either image,audio or null ');
  }
  if (data.questionMediaType && !data.questionMedia) {
    errors.push('Url of question media is required when question media type is specified.');
  }
  if (errors.length > 0) {
    return {
      isValid: false,
      message: errors,
    };
  }
  return {
    isValid: true,
    message: ['Valid question media type'],
  };
};

const questionMediaValidation = (data: MediumMapping) => {
  const errors: string[] = [];
  if (typeof data.questionMedia !== 'string' && data.questionMedia !== null) {
    errors.push('questionMedia must be either a string or null');
  }
  return errors.length > 0
    ? { isValid: false, message: errors }
    : { isValid: true, message: ['Valid question media'] };
};

const explanationValidation = (data: Explanation) => {
  const errors: string[] = [];
  if (typeof data.explanation !== 'string' && data.explanation !== null) {
    errors.push('Explanation must be either a string or null');
  }
  return errors.length > 0
    ? { isValid: false, message: errors }
    : { isValid: true, message: ['Valid explanation'] };
};

const explanationTypeValidation = (data: Explanation) => {
  const errors: string[] = [];
  const validExplanationTypes = Object.values(ExplanationTypeEnum);
  if (data.explanationType !== null && !validExplanationTypes.includes(data.explanationType)) {
    errors.push(
      'explanationType must be one of the following: text, image, audio, video, pdf, or null',
    );
  }
  return errors.length > 0
    ? { isValid: false, message: errors }
    : { isValid: true, message: ['Valid explanation type'] };
};

const hintValidation = (data: Hint[]) => {
  const errors: string[] = [];
  if (!Array.isArray(data)) {
    errors.push('Hint must be an array of object');
  }
  if (data.length === 0) {
    return {
      isValid: true,
      message: ['Valid hint'],
    };
  }
  for (const [index, hint] of data.entries()) {
    if (typeof hint.value !== 'string' || hint.value.trim() === '') {
      errors.push(`Hint at index ${index} must have a non-empty string value.`);
    }
    if (!Object.values(HintTypeEnum).includes(hint.type)) {
      errors.push(`Hint at index ${index} must have a valid type ('text' or 'image').`);
    }
  }
  return errors.length > 0
    ? { isValid: false, message: errors }
    : { isValid: true, message: ['Valid hint'] };
};

export const validateQuestion = (data: QuestionType) => {
  const questionFormatValidators: {
    [format in QuestionType['format']]: (
      quesionFormatData: QuestionType['mediumMapping']['']['metadata']['questionFormatData'],
    ) => { isValid: boolean; message: string[] };
  } = {
    mcq: (quesionFormatData) => validationFunctionForMcq(quesionFormatData as McqFormat),
    fill_in_the_blanks: (quesionFormatData) =>
      validationFunctionForFillInTheBlanks(quesionFormatData as FillInTheBlanksFormat),
    match_the_following: (questionFormatData) =>
      validationFunctionForMatchTheFollowing(questionFormatData as MatchTheFollowingFormat),
    sequencing: (questionFormatData) =>
      validationFunctionForSequencing(questionFormatData as SequencingFormat),
    subjective_rearrange: (questionFormatData) =>
      validationFunctionSubjectRearrange(questionFormatData as SubjectiveRearrangeFormat),
    arrange_the_following: (questionFormatData) =>
      validationFunctionForArrangeTheFollowing(questionFormatData as ArrangeTheFollowingFormat),
    one_word: (questionFormatData) =>
      validationFunctionForOneWord(questionFormatData as OneWordFormat),
    error_analysis: (quesionFormatData) =>
      validationFunctionForErrorAnalysis(quesionFormatData as ErrorAnalysisFormat),
  };
  return (
    Object.keys(data.mediumMapping)
      .map((language: string) => {
        const questionFormatData = questionFormatValidators[data.format](
          data.mediumMapping[language].metadata.questionFormatData,
        );
        const questionText = questionTextValidation(data.mediumMapping[language]);
        const questionMediaType = questionMediaTypeValidation(data.mediumMapping[language]);
        const questionMedia = questionMediaValidation(data.mediumMapping[language]);
        const explanation = explanationValidation(data.mediumMapping[language].metadata);
        const explanationType = explanationTypeValidation(data.mediumMapping[language].metadata);
        const hints = hintValidation(data.mediumMapping[language].metadata.hints);
        const all = {
          questionFormatData,
          questionText,
          questionMediaType,
          questionMedia,
          explanation,
          explanationType,
          hints,
        };

        return {
          language,
          // eslint-disable-next-line unicorn/no-array-reduce
          result: Object.keys(all).reduce(
            (accumulator, key) => {
              const { isValid, message } = all[key as keyof typeof all];
              accumulator.isValid = accumulator.isValid && isValid;
              accumulator.message = [
                ...accumulator.message,
                ...(isValid ? [] : message.map((errrorString) => `${data.id} : ${errrorString}`)),
              ];
              return accumulator;
            },
            { isValid: true, message: [] } as { isValid: boolean; message: string[] },
          ),
        };
      })
      // eslint-disable-next-line unicorn/no-array-reduce
      .reduce(
        (accumulator, mediumError) => {
          return { [mediumError.language]: mediumError.result, ...accumulator };
        },
        {} as Record<string, { isValid: boolean; message: string[] }>,
      )
  );
};

const validateMcqMapping = (
  mainMediumMapping: MediumMapping,
  optionalMediumMapping: MediumMapping,
) => {
  const errors: string[] = [];
  const mainMediumOptions = (mainMediumMapping.metadata.questionFormatData as McqFormat).options;
  const optionalMediumOptions = (optionalMediumMapping.metadata.questionFormatData as McqFormat)
    .options;
  const sameLength = mainMediumOptions.length === optionalMediumOptions.length;
  const sameType = optionalMediumOptions.every(
    (option) => option.type === mainMediumOptions[0].type,
  );
  if (!sameLength) {
    errors.push(
      `Number of options (${optionalMediumOptions.length}) not same as main medium options (${mainMediumOptions.length})`,
    );
  }
  if (!sameType) {
    errors.push(`Type of options is not same as main medium options`);
  }
  return {
    isValid: sameLength && sameType,
    errors,
  };
};

const validateMappings = (
  validatedMediumMapping: {
    [languageCode: string]: MediumMapping;
  },
  mediumPriority: string[],
  format: QuestionType['format'],
  id: QuestionType['id'],
) => {
  let validMediums: string[] = [];
  const errors: string[] = [];
  switch (format) {
    case QuestionFormatEnum.mcq: {
      validMediums.push(mediumPriority[0]); // assumes validatedMediumMapping[mediumPriority[0]] is valid
      for (const medium of mediumPriority) {
        if (validatedMediumMapping[medium]) {
          const { isValid, errors: mediumMappingErrors } = validateMcqMapping(
            validatedMediumMapping[mediumPriority[0]],
            validatedMediumMapping[medium],
          );
          if (isValid) {
            validMediums.push(medium);
          } else {
            errors.push(
              ...mediumMappingErrors.map((errorMessage) => `${medium} : ${id} : ${errorMessage}`),
            );
          }
        }
      }
      break;
    }

    default: {
      validMediums = Object.keys(validatedMediumMapping);
      break;
    }
  }
  return { validMediums, errors };
};

interface ValidationResult {
  isValid: boolean;
  message: string[];
}

export const getValidatedQuestion = (data: QuestionType, mediumPriority: string[]) => {
  let mediumWiseValidationResult: { [key: string]: ValidationResult } = {};
  try {
    mediumWiseValidationResult = validateQuestion(data);
  } catch (error_) {
    logger.error('Error validating question data:', error_);
    return {
      isValid: false,
      errors: ['Could not validate question.'],
    };
  }
  const validatedMediumMapping: { [languageCode: string]: MediumMapping } = {};
  const errors: string[] = [];
  for (const medium of mediumPriority) {
    if (data.mediumMapping[medium]) {
      if (mediumWiseValidationResult[medium].isValid) {
        validatedMediumMapping[medium] = data.mediumMapping[medium];
      } else {
        logger.error(
          `Question validation failed`,
          JSON.stringify({
            questionId: data.id,
            medium,
            questionFormat: data.format,
            cmsQuestionId: data.mediumMapping[medium].cmsQuestionId,
          }),
        );

        errors.push(
          ...mediumWiseValidationResult[medium].message.map(
            (errorMessage) => `${medium} : ${errorMessage}`,
          ),
        );
      }
    }
  }
  const isMainMediumPresent = !!data.mediumMapping[mediumPriority[0]];
  const isMainMediumValid = !!validatedMediumMapping[mediumPriority[0]];
  const multipleMediumPresent = Object.keys(validatedMediumMapping).length > 1;
  let mediumMapping = validatedMediumMapping;
  if (isMainMediumPresent && multipleMediumPresent) {
    const { validMediums, errors: allMediumMappingErrors } = validateMappings(
      validatedMediumMapping,
      mediumPriority,
      data.format,
      data.id,
    );
    mediumMapping = {};
    for (const medium of validMediums) {
      // assumes validMediums will atleast include mediumPriority[0]
      if (validatedMediumMapping[medium]) mediumMapping[medium] = validatedMediumMapping[medium];
    }
    errors.push(...allMediumMappingErrors);
  }

  return {
    isValid: isMainMediumValid,
    question: { ...data, mediumMapping },
    errors,
    toBeIgnored: !isMainMediumPresent,
  };
};

export const getValidatedQuestionList = (
  questionList: QuestionType[],
  mediumPriority: string[],
) => {
  const questions: QuestionType[] = [];
  let allErrors: string[] = [];
  for (const orginalQuestion of questionList) {
    const { isValid, question, errors, toBeIgnored } = getValidatedQuestion(
      orginalQuestion,
      mediumPriority,
    );
    if (isValid && question) {
      questions.push(question);
      if (errors.length > 0) {
        logger.info('Some errors present which are ignored ', orginalQuestion, errors);
        allErrors = [...allErrors, ...errors];
      }
    } else if (errors.length > 0) {
      logger.info(
        toBeIgnored ? 'Invalid question present in optional medium' : 'Invalid question',
        orginalQuestion,
        errors,
      );
      allErrors = [...allErrors, ...errors];
    }
  }
  return { questions, errors: allErrors };
};

export const getValidatedQuestionsForCueing = (
  formativeQuestions: QuestionType[],
  pivotQuestions: QuestionType[],
  maxDifficulty: number,
) => {
  const errors: string[] = [];
  const difficultyWiseQuestions: { [difficulty: number]: QuestionType[] } = {};
  let minDifficulty: number | undefined;
  for (const question of formativeQuestions) {
    if (question.difficulty !== undefined) {
      if (!difficultyWiseQuestions[question.difficulty]) {
        difficultyWiseQuestions[question.difficulty] = [];
      }
      difficultyWiseQuestions[question.difficulty].push(question);
      if (minDifficulty === undefined || question.difficulty < minDifficulty) {
        minDifficulty = question.difficulty;
      }
    }
  }
  for (const question of pivotQuestions) {
    if (question.type === 2 || question.type === 4) {
      errors.push(`${question.id} : Pivot Questions should not have questions of type 2 & 4`);
    }
  }
  const uniqueDifficulties = Object.keys(difficultyWiseQuestions);
  // for (const key of uniqueDifficulties) {
  //   const difficulty = Number.parseInt(key, 10);
  //   if (difficultyWiseQuestions[difficulty].length < 3 && difficulty === maxDifficulty) {
  //     errors.push('Number of question of maxDifficulty is less than 3');
  //   }
  // } else {
  //   errors.push(`Number of question of difficulty ${difficulty} is less than 3`);
  // }
  // }
  if (
    !difficultyWiseQuestions[maxDifficulty] ||
    difficultyWiseQuestions[maxDifficulty].length < 3
  ) {
    errors.push(
      `Number of questions of maxDifficulty(${maxDifficulty}) is ${
        difficultyWiseQuestions[maxDifficulty]?.length || 0
      }`,
    );
  }
  if (pivotQuestions.length < 3) {
    errors.push(`Number of pivot questions is ${pivotQuestions.length}`);
  }
  return {
    isValid: errors.length === 0,
    errors,
    difficultyWiseQuestions,
    minDifficulty,
    totalSets: errors.length === 0 ? uniqueDifficulties.length + 1 : 1,
  };
};

export const getValidatedDataForCueingEngine = (
  getQuestionsResponse: GetQuestionsResponse,
  getStatsResponse: GetStatsResponse,
) => {
  const { questions: formativeQuestions, errors: formativeErrors } = getValidatedQuestionList(
    getQuestionsResponse?.formativeQuestions || [],
    getQuestionsResponse.mediumPriority,
  );
  const { questions: pivotQuestions, errors: pivotErrors } = getValidatedQuestionList(
    getQuestionsResponse?.pivotQuestions || [],
    getQuestionsResponse.mediumPriority,
  );

  const maxDifficulty = getStatsResponse.maxStageDifficulty;

  const {
    isValid,
    errors: cueingErrors,
    difficultyWiseQuestions,
    minDifficulty,
    totalSets,
  } = getValidatedQuestionsForCueing(formativeQuestions, pivotQuestions, maxDifficulty);

  return {
    isValid,
    errors: [...formativeErrors, ...pivotErrors, ...cueingErrors],
    difficultyWiseQuestions,
    minDifficulty,
    maxDifficulty,
    formativeQuestions,
    pivotQuestions,
    totalSets,
  };
};

export const getValidatedDataForPracticeCueingEngine = (
  getPracticeContentResponse: PracticeContentResponse,
) => {
  const content: ContentItem[] = [];
  const overallErrors: string[] = [];
  for (const skill of getPracticeContentResponse.content) {
    const { questions, errors } = getValidatedQuestionList(
      skill.questions,
      getPracticeContentResponse.mediumPriority,
    );
    if (questions.length > 0) {
      content.push({ ...skill, questions });
    }
    if (errors.length > 0) {
      overallErrors.push(...errors);
    }
  }

  return {
    isValid: content.length > 0,
    errors: overallErrors,
    content,
  };
};
